import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { posthog } from 'posthog-js';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { RootState } from '../../../reducers';
import { usePlan } from '../hooks/plan';
import { createOrUpdateSubscription } from '../thunk';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { setAlertMessage } from '../../dashboard/dashboardSlice';
import { usePricing } from '../../../hooks/usePricing';
import {
  getCancellationInfo,
  scheduleCancellation,
  acceptRetentionOffer,
  reactivateSubscription,
  CancellationInfo,
} from '../../../services/cancellationServices';
import {
  CancellationConfirmModal,
  RetentionOfferModal,
  CancellationSuccessModal,
  invalidateCancellationInfoCache,
} from '../../settings/billing/cancellation';
import { trackCancelPlanButton } from '../planSlice';
import { trackCancelTrial } from '../../../lib/tracking';
import logger from '../../../lib/logger';

// ---------------------------------------------------------------------------
// Preload – called from UpsellButton so data is ready when the modal opens
// ---------------------------------------------------------------------------
let preloadPromise: Promise<CancellationInfo | null> | null = null;
let preloadedInfo: CancellationInfo | null = null;

export const preloadTrialInfo = (teamId: string) => {
  if (preloadedInfo || preloadPromise) return;
  preloadPromise = getCancellationInfo({ teamId })
    .then((info) => {
      preloadedInfo = info;
      return info;
    })
    .catch(() => null);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ManageTrialModal = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { team, plan } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0].teamId;
  const { package: teamPackage, term } = plan.subscription;
  const { teamPlan } = usePlan();
  const { getYearlyPrice, getTrialModalPrice } = usePricing();

  const monthlyPrice = getTrialModalPrice();
  const yearlyPrice = getYearlyPrice(monthlyPrice);

  // ---- cancellation info (preloaded or fetched on mount) ----
  const [info, setInfo] = useState<CancellationInfo | null>(preloadedInfo);

  useEffect(() => {
    if (info) return;
    const fetch = async () => {
      try {
        const result = preloadPromise
          ? await preloadPromise
          : await getCancellationInfo({ teamId });
        if (result) setInfo(result);
      } catch {
        // modal still usable without this data
      }
    };
    fetch();
  }, [teamId, info]);

  // ---- derived display values ----
  const trialEndDate = info
    ? new Date(info.currentPeriodEnd).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const daysLeft = info
    ? Math.max(
        0,
        Math.ceil(
          (new Date(info.currentPeriodEnd).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24),
        ),
      )
    : null;

  const isCancelled = info?.isAlreadyCanceled ?? false;
  const cancellationEndDate = info
    ? new Date(info.cancelAt || info.currentPeriodEnd).toLocaleDateString(
        'en-US',
        {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        },
      )
    : null;

  const getCancelledDescription = () => {
    if (cancellationEndDate) {
      return t('upgrade.manageTrial.cancelledDescription', {
        date: cancellationEndDate,
      });
    }
    return t('upgrade.manageTrial.cancelledDescriptionNoDate');
  };

  const getActiveDescription = () => {
    if (daysLeft !== null && trialEndDate) {
      return t('upgrade.manageTrial.description', {
        count: daysLeft,
        date: trialEndDate,
      });
    }
    return t('upgrade.manageTrial.descriptionNoDate');
  };

  const description = isCancelled
    ? getCancelledDescription()
    : getActiveDescription();

  // ---- modal visibility ----
  const [mainDialogOpen, setMainDialogOpen] = useState(true);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRetention, setShowRetention] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [cancelDate, setCancelDate] = useState('');
  const [cancelSuccessMessage, setCancelSuccessMessage] = useState('');

  // ---- handlers: start plan now ----
  const handleStartPlan = async () => {
    setIsUpgrading(true);
    try {
      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: teamPlan || 'GROWTH',
          term: 'annual',
          totalAmount: yearlyPrice,
        }),
      );
      if (createOrUpdateSubscription.rejected.match(result as never)) {
        const { message = '' } = (result as any).error || {};
        throw new Error(message);
      }
      setShowStartConfirm(false);
      onClose();
      dispatch(pushModal({ type: 'upgradeSuccess' }));
    } catch (error: any) {
      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('upgrade.manageTrial.upgradeError', {
            error: error.message,
          }),
        }),
      );
    } finally {
      setIsUpgrading(false);
    }
  };

  // ---- handlers: go unlimited ----
  const handleGoUnlimited = () => {
    onClose();
    dispatch(pushModal({ type: 'upsell-modal', source: 'manage-trial' }));
  };

  // ---- handlers: cancel trial (self-managed, no extra fetch) ----
  const performCancel = async () => {
    try {
      const result = await scheduleCancellation({ teamId });
      invalidateCancellationInfoCache();
      setCancelDate(result.cancelAt);
      setCancelSuccessMessage(result.message);
      setShowCancelSuccess(true);

      trackCancelTrial({ plan: teamPackage, term, cancelAt: result.cancelAt });
      posthog.capture('trial_cancelled', {
        plan: teamPackage,
        term,
        cancelAt: result.cancelAt,
      });
    } catch (error) {
      logger.error('Failed to cancel trial:', error);
      dispatch(
        setAlertMessage({
          type: 'error',
          message: String(error),
        }),
      );
    }
  };

  const handleCancelTrial = () => {
    dispatch(trackCancelPlanButton({ button: 'cancel-subscription' }));
    setMainDialogOpen(false);

    if (info?.isAlreadyCanceled) {
      setCancelDate(info.cancelAt || info.currentPeriodEnd);
      setCancelSuccessMessage(
        'Your subscription is already scheduled for cancellation.',
      );
      setShowCancelSuccess(true);
      return;
    }
    setShowCancelConfirm(true);
  };

  const handleCancelConfirmProceed = () => {
    setShowCancelConfirm(false);

    if (info?.eligibleForRetention && info?.retentionOffer) {
      setShowRetention(true);
    } else {
      performCancel();
    }
  };

  const handleAcceptOffer = async () => {
    if (!info?.retentionOffer) return;
    try {
      await acceptRetentionOffer({
        teamId,
        offerType: info.retentionOffer.type,
      });
      setShowRetention(false);
      invalidateCancellationInfoCache();
      window.location.reload();
    } catch (error) {
      logger.error('Failed to accept retention offer:', error);
      setShowRetention(false);
    }
  };

  const handleDeclineOffer = async () => {
    setShowRetention(false);
    await performCancel();
  };

  // ---- handlers: reactivate cancelled trial ----
  const handleReactivateTrial = async () => {
    setIsReactivating(true);
    try {
      await reactivateSubscription({ teamId });
      invalidateCancellationInfoCache();
      preloadedInfo = null;
      preloadPromise = null;
      window.location.reload();
    } catch (error) {
      logger.error('Failed to reactivate trial:', error);
      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('upgrade.manageTrial.reactivateError'),
        }),
      );
    } finally {
      setIsReactivating(false);
    }
  };

  const handleCloseCancelFlow = () => {
    setShowCancelConfirm(false);
    setShowRetention(false);
    setShowCancelSuccess(false);
    onClose();
  };

  const handleStartConfirmClose = () => {
    setShowStartConfirm(false);
    onClose();
  };

  return (
    <>
      {/* Main manage trial dialog */}
      <Dialog open={mainDialogOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>
              {isCancelled
                ? t('upgrade.manageTrial.cancelledTitle')
                : t('upgrade.manageTrial.title')}
            </DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 py-2">
            {isCancelled ? (
              <Button
                onClick={handleReactivateTrial}
                disabled={isReactivating}
                className="w-full gap-2"
                size="lg"
              >
                {isReactivating && <Loader className="h-4 w-4 animate-spin" />}
                {isReactivating
                  ? t('upgrade.manageTrial.reactivating')
                  : t('upgrade.manageTrial.reactivateTrial')}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => {
                    setMainDialogOpen(false);
                    setShowStartConfirm(true);
                  }}
                  className="w-full"
                  size="lg"
                >
                  {t('upgrade.manageTrial.startPlanNow')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleGoUnlimited}
                  className="w-full"
                  size="lg"
                >
                  {t('upgrade.manageTrial.goUnlimited')}
                </Button>
                <button
                  type="button"
                  onClick={handleCancelTrial}
                  className="mt-1 text-sm text-muted-foreground underline hover:text-foreground transition-colors"
                >
                  {t('upgrade.manageTrial.cancelTrial')}
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Start plan confirmation modal */}
      <Dialog
        open={showStartConfirm}
        onOpenChange={(open) => !open && handleStartConfirmClose()}
      >
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle>{t('upgrade.manageTrial.startPlanNow')}</DialogTitle>
            <DialogDescription>
              {t('upgrade.manageTrial.startPlanConfirm', {
                price: yearlyPrice,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={handleStartPlan}
              disabled={isUpgrading}
              className="w-full gap-2"
              size="lg"
            >
              {isUpgrading && <Loader className="h-4 w-4 animate-spin" />}
              {t('upgrade.manageTrial.confirmAndStart')}
            </Button>
            <Button
              variant="ghost"
              onClick={handleStartConfirmClose}
              disabled={isUpgrading}
              className="w-full"
            >
              {t('upgrade.manageTrial.back')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation flow modals */}
      <CancellationConfirmModal
        open={showCancelConfirm}
        onClose={handleCloseCancelFlow}
        onProceed={handleCancelConfirmProceed}
        cancellationInfo={info}
      />
      <RetentionOfferModal
        open={showRetention}
        onClose={handleCloseCancelFlow}
        onAccept={handleAcceptOffer}
        onDecline={handleDeclineOffer}
        retentionOffer={info?.retentionOffer || null}
        subscriptionType={info?.subscriptionType || 'trial'}
      />
      <CancellationSuccessModal
        open={showCancelSuccess}
        onClose={handleCloseCancelFlow}
        cancelAt={cancelDate}
        message={cancelSuccessMessage}
      />
    </>
  );
};

export default ManageTrialModal;
